import { cegidCON } from './cegid';
import type { CegidFactura } from './cegid/types';

export interface SyncResult {
  syncRunId: string;
  status: 'completed' | 'failed';
  recordsProcessed: number;
  recordsSuccess: number;
  recordsFailed: number;
  durationMs: number;
  errors?: string[];
}

/**
 * Core sync engine — fetches data from Cegid and stores in reporting tables.
 * Used by both /api/cegid/sync (admin manual) and /api/webhooks/sync-cron (n8n).
 * This function ONLY reads from Cegid and writes to Supabase reporting tables.
 */
export async function executeSyncForCompany(
  supabaseAdmin: any,
  companyCode: string,
  syncRunId: string,
  options?: {
    syncTypes?: string[];
    ejercicio?: string;
    tipoOpcionFiscal?: number;
  }
): Promise<SyncResult> {
  const currentYear = options?.ejercicio || new Date().getFullYear().toString();
  const tipoFiscal = options?.tipoOpcionFiscal ?? 1;

  const defaultTypes = tipoFiscal === 1
    ? ['facturas', 'apuntes', 'subcuentas']
    : ['facturasEstimaciones', 'clientes', 'proveedores'];
  const typesToSync = options?.syncTypes || defaultTypes;

  const startTime = Date.now();
  let recordsProcessed = 0;
  let recordsSuccess = 0;
  let recordsFailed = 0;
  const errors: string[] = [];

  try {
    // Sync facturas
    if (typesToSync.includes('facturas')) {
      try {
        const facturas = await cegidCON.getFacturas(companyCode, currentYear, { $top: 500 });
        for (const factura of facturas) {
          try {
            await supabaseAdmin.from('cegid_raw_records').insert({
              sync_run_id: syncRunId,
              record_type: 'factura',
              external_id: factura.numero || factura.id || null,
              raw_data: factura,
            });
            recordsSuccess++;
          } catch {
            recordsFailed++;
          }
          recordsProcessed++;
        }
      } catch (e: any) {
        errors.push(`Facturas sync error: ${e.message}`);
      }
    }

    // Sync apuntes
    if (typesToSync.includes('apuntes')) {
      try {
        const apuntes = await cegidCON.getApuntes(companyCode, currentYear, { $top: 1000 });
        for (const apunte of apuntes) {
          try {
            await supabaseAdmin.from('cegid_raw_records').insert({
              sync_run_id: syncRunId,
              record_type: 'apunte',
              external_id: apunte.nroAsiento || null,
              raw_data: apunte,
            });
            recordsSuccess++;
          } catch {
            recordsFailed++;
          }
          recordsProcessed++;
        }
      } catch (e: any) {
        errors.push(`Apuntes sync error: ${e.message}`);
      }
    }

    // Sync subcuentas
    if (typesToSync.includes('subcuentas')) {
      try {
        const subcuentas = await cegidCON.getSubcuentas(companyCode, currentYear);
        for (const subcuenta of subcuentas) {
          try {
            await supabaseAdmin.from('cegid_raw_records').insert({
              sync_run_id: syncRunId,
              record_type: 'subcuenta',
              external_id: subcuenta.codigo || null,
              raw_data: subcuenta,
            });
            recordsSuccess++;
          } catch {
            recordsFailed++;
          }
          recordsProcessed++;
        }
      } catch (e: any) {
        errors.push(`Subcuentas sync error: ${e.message}`);
      }
    }

    // Sync facturasEstimaciones (for tipoOpcionFiscal 2|3|4)
    if (typesToSync.includes('facturasEstimaciones')) {
      for (const tipo of ['recibidas', 'emitidas'] as const) {
        try {
          const facturas = await cegidCON.getFacturasEstimaciones(companyCode, tipo, currentYear, { $top: 500 });
          for (const factura of facturas) {
            try {
              await supabaseAdmin.from('cegid_raw_records').insert({
                sync_run_id: syncRunId,
                record_type: `factura_estimacion_${tipo}`,
                external_id: factura.numero || factura.id || null,
                raw_data: factura,
              });
              recordsSuccess++;
            } catch {
              recordsFailed++;
            }
            recordsProcessed++;
          }
        } catch (e: any) {
          errors.push(`FacturasEstimaciones ${tipo} sync error: ${e.message}`);
        }
      }
    }

    // Sync clientes from CON
    if (typesToSync.includes('clientes')) {
      try {
        const clientes = await cegidCON.getClientes(companyCode);
        for (const cliente of clientes) {
          try {
            await supabaseAdmin.from('cegid_raw_records').insert({
              sync_run_id: syncRunId,
              record_type: 'cliente_con',
              external_id: cliente.codigo || null,
              raw_data: cliente,
            });
            recordsSuccess++;
          } catch {
            recordsFailed++;
          }
          recordsProcessed++;
        }
      } catch (e: any) {
        errors.push(`Clientes CON sync error: ${e.message}`);
      }
    }

    // Sync proveedores from CON
    if (typesToSync.includes('proveedores')) {
      try {
        const proveedores = await cegidCON.getProveedores(companyCode);
        for (const proveedor of proveedores) {
          try {
            await supabaseAdmin.from('cegid_raw_records').insert({
              sync_run_id: syncRunId,
              record_type: 'proveedor',
              external_id: proveedor.codigo || null,
              raw_data: proveedor,
            });
            recordsSuccess++;
          } catch {
            recordsFailed++;
          }
          recordsProcessed++;
        }
      } catch (e: any) {
        errors.push(`Proveedores sync error: ${e.message}`);
      }
    }

    const durationMs = Date.now() - startTime;
    const finalStatus = errors.length === 0 ? 'completed' : (recordsSuccess > 0 ? 'completed' : 'failed');

    // Update sync run record
    await supabaseAdmin
      .from('cegid_sync_runs')
      .update({
        status: finalStatus,
        completed_at: new Date().toISOString(),
        duration_ms: durationMs,
        records_processed: recordsProcessed,
        records_success: recordsSuccess,
        records_failed: recordsFailed,
        error_message: errors.length > 0 ? errors.join('; ') : null,
      })
      .eq('id', syncRunId);

    // Process raw records into reporting tables
    await processRawRecordsToReporting(supabaseAdmin, syncRunId, companyCode);

    return {
      syncRunId,
      status: finalStatus,
      recordsProcessed,
      recordsSuccess,
      recordsFailed,
      durationMs,
      errors: errors.length > 0 ? errors : undefined,
    };
  } catch (error: any) {
    const durationMs = Date.now() - startTime;
    await supabaseAdmin
      .from('cegid_sync_runs')
      .update({
        status: 'failed',
        completed_at: new Date().toISOString(),
        duration_ms: durationMs,
        error_message: error.message,
      })
      .eq('id', syncRunId);

    return {
      syncRunId,
      status: 'failed',
      recordsProcessed,
      recordsSuccess,
      recordsFailed,
      durationMs,
      errors: [error.message],
    };
  }
}

/**
 * Process raw Cegid records into reporting tables (tax_summary, tax_iva_detail).
 * Normalization step: cegid_raw_records → reporting tables → dashboards read from there.
 */
export async function processRawRecordsToReporting(
  supabaseAdmin: any,
  syncRunId: string,
  companyCode: string
) {
  try {
    const { data: rawFacturas } = await supabaseAdmin
      .from('cegid_raw_records')
      .select('id, raw_data')
      .eq('sync_run_id', syncRunId)
      .eq('record_type', 'factura')
      .eq('processed', false);

    if (!rawFacturas || rawFacturas.length === 0) return;

    const { data: mapping } = await supabaseAdmin
      .from('cegid_company_mapping')
      .select('cliente_id')
      .eq('cegid_company_code', companyCode)
      .eq('active', true)
      .maybeSingle();

    if (!mapping?.cliente_id) {
      console.warn(`[Sync] No client mapping found for company code: ${companyCode}`);
      return;
    }

    const clienteId = mapping.cliente_id;

    const periodAggregations: Record<string, {
      iva_repercutido: number;
      iva_soportado: number;
      base_emitidas: number;
      base_recibidas: number;
      retenciones_practicadas: number;
      retenciones_soportadas: number;
      suplidos: number;
      num_emitidas: number;
      num_recibidas: number;
      ivaDetails: Array<{ tipo: string; porcentaje: number; base: number; cuota: number }>;
    }> = {};

    for (const raw of rawFacturas) {
      const f: CegidFactura = raw.raw_data;
      const fecha = f.fecha ? new Date(f.fecha) : new Date();
      const year = fecha.getFullYear();
      const month = fecha.getMonth() + 1;
      const periodo = `${year}-${String(month).padStart(2, '0')}`;

      if (!periodAggregations[periodo]) {
        periodAggregations[periodo] = {
          iva_repercutido: 0, iva_soportado: 0,
          base_emitidas: 0, base_recibidas: 0,
          retenciones_practicadas: 0, retenciones_soportadas: 0,
          suplidos: 0, num_emitidas: 0, num_recibidas: 0,
          ivaDetails: [],
        };
      }

      const agg = periodAggregations[periodo];
      const isEmitida = f.tipoFactura === 'emitida' || f.tipoFactura === 'E';

      for (let i = 1; i <= 4; i++) {
        const base = Number(f[`baseImponible${i}`]) || 0;
        const cuota = Number(f[`cuotaIVA${i}`]) || 0;
        const pct = Number(f[`porcentajeIVA${i}`]) || 0;

        if (base !== 0 || cuota !== 0) {
          if (isEmitida) {
            agg.iva_repercutido += cuota;
            agg.base_emitidas += base;
          } else {
            agg.iva_soportado += cuota;
            agg.base_recibidas += base;
          }

          agg.ivaDetails.push({
            tipo: isEmitida ? 'emitida' : 'recibida',
            porcentaje: pct,
            base,
            cuota,
          });
        }
      }

      const retencion = Number(f.cuotaRetencion) || 0;
      if (isEmitida) {
        agg.retenciones_soportadas += retencion;
      } else {
        agg.retenciones_practicadas += retencion;
      }

      agg.suplidos += Number(f.suplidos) || 0;

      if (isEmitida) agg.num_emitidas++;
      else agg.num_recibidas++;

      await supabaseAdmin
        .from('cegid_raw_records')
        .update({ processed: true, processed_at: new Date().toISOString() })
        .eq('id', raw.id);
    }

    for (const [periodo, agg] of Object.entries(periodAggregations)) {
      const year = periodo.split('-')[0];

      await supabaseAdmin.from('tax_summary').upsert({
        cliente_id: clienteId,
        periodo,
        ejercicio_fiscal: year,
        iva_repercutido: agg.iva_repercutido,
        iva_soportado: agg.iva_soportado,
        iva_resultado: agg.iva_repercutido - agg.iva_soportado,
        base_imponible_emitidas: agg.base_emitidas,
        base_imponible_recibidas: agg.base_recibidas,
        retenciones_practicadas: agg.retenciones_practicadas,
        retenciones_soportadas: agg.retenciones_soportadas,
        total_suplidos: agg.suplidos,
        num_facturas_emitidas: agg.num_emitidas,
        num_facturas_recibidas: agg.num_recibidas,
        source: 'cegid_sync',
        last_sync_at: new Date().toISOString(),
      }, { onConflict: 'cliente_id,periodo' });

      const { data: summary } = await supabaseAdmin
        .from('tax_summary')
        .select('id')
        .eq('cliente_id', clienteId)
        .eq('periodo', periodo)
        .single();

      if (summary) {
        await supabaseAdmin
          .from('tax_iva_detail')
          .delete()
          .eq('tax_summary_id', summary.id);

        const ivaMap = new Map<string, { tipo: string; pct: number; base: number; cuota: number; count: number }>();
        for (const d of agg.ivaDetails) {
          const key = `${d.tipo}:${d.porcentaje}`;
          const existing = ivaMap.get(key);
          if (existing) {
            existing.base += d.base;
            existing.cuota += d.cuota;
            existing.count++;
          } else {
            ivaMap.set(key, { tipo: d.tipo, pct: d.porcentaje, base: d.base, cuota: d.cuota, count: 1 });
          }
        }

        for (const detail of ivaMap.values()) {
          await supabaseAdmin.from('tax_iva_detail').insert({
            tax_summary_id: summary.id,
            tipo_factura: detail.tipo,
            porcentaje_iva: detail.pct,
            base_imponible: detail.base,
            cuota_iva: detail.cuota,
            num_facturas: detail.count,
          });
        }
      }
    }

    console.log(`[Sync] Processed ${rawFacturas.length} facturas into reporting tables for company ${companyCode}`);
  } catch (error: any) {
    console.error('[Sync] Error processing raw records to reporting:', error);
  }
}
